import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button, Card, Input, Typography, Row, Col, Alert } from 'antd';
import { useToast } from '../../contexts/ToastContext';
import { SafetyOutlined, ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import { queryClient } from '../../lib/queryClient';
import '../../styles/colors.css';

const { Title, Text } = Typography;

const VerifyEmail: React.FC = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [success, setSuccess] = useState(false);
  const [verifySuccessText, setVerifySuccessText] = useState('');

  const token = searchParams.get('token');
  const codeFromUrl = searchParams.get('code');

  useEffect(() => {
    if (codeFromUrl) {
      setCode(codeFromUrl);
    }
  }, [codeFromUrl]);

  const validateForm = () => {
    let isValid = true;
    setCodeError('');

    if (!code) {
      setCodeError('Doğrulama kodunu girin!');
      isValid = false;
    } else if (code.length !== 6) {
      setCodeError('Doğrulama kodu 6 haneli olmalıdır!');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!validateForm()) {
      return;
    }

    if (!token) {
      toast.error('Geçersiz doğrulama linki!');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.verifyRegistration(token, code);
      if (response.success) {
        setVerifySuccessText(
          response.message ||
            'E-posta adresiniz başarıyla doğrulandı! Hesabınız aktifleştirildi.'
        );
        setSuccess(true);
        toast.success(
          response.message || 'E-posta adresiniz başarıyla doğrulandı! Hesabınız aktifleştirildi.'
        );

        const data = response.data;
        if (data?.token) {
          queryClient.clear();
          localStorage.setItem('token', data.token);
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }
          if (data.email && data.role) {
            localStorage.setItem('user', JSON.stringify({
              email: data.email,
              role: data.role,
              name: data.fullName || '',
              fullName: data.fullName || '',
              id: data.userId
            }));
          }
          setTimeout(() => {
            window.location.replace('/');
          }, 1500);
        } else {
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      } else {
        toast.error(response.message || 'E-posta doğrulama başarısız oldu.');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Bir hata oluştu. Lütfen tekrar deneyin.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, minHeight: '100vh' }}>
        <div style={{ width: '100%', maxWidth: 500 }}>
        <Card className="app-content-card" style={{ padding: 24 }}>
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <SafetyOutlined style={{ fontSize: '64px', color: 'var(--error)', marginBottom: '16px' }} />
            <Title level={2}>Geçersiz Link</Title>
            <Text type="secondary">Doğrulama linki geçersiz veya eksik.</Text>
          </div>
          <Link to="/login">
            <Button type="primary" block size="large">
              Giriş Sayfasına Dön
            </Button>
          </Link>
        </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, minHeight: '100vh' }}>
        <div style={{ width: '100%', maxWidth: 500 }}>
        <Card className="app-content-card" style={{ padding: 24 }}>
          <div style={{ textAlign: 'center' }}>
            <CheckCircleOutlined style={{ fontSize: '64px', color: 'var(--success)', marginBottom: '16px' }} />
            <Title level={2} style={{ color: 'var(--success)' }}>Doğrulama Başarılı!</Title>
            <Text>{verifySuccessText || 'E-posta adresiniz başarıyla doğrulandı.'}</Text>
            <br />
            <Text type="secondary">Yönlendiriliyorsunuz...</Text>
          </div>
        </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, minHeight: '100vh' }}>
      <div style={{ width: '100%', maxWidth: 500 }}>
      <Card className="app-content-card" style={{ padding: 24 }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <SafetyOutlined style={{ fontSize: '64px', color: 'var(--primary-600)', marginBottom: '16px' }} />
          <Title level={2}>E-posta Doğrulama</Title>
          <Text type="secondary">E-posta adresinize gönderilen doğrulama kodunu girin</Text>
        </div>

        <Alert
          message="Doğrulama Kodu"
          description="E-posta adresinize gönderilen 6 haneli doğrulama kodunu girin. Link üzerinden geldiyseniz kod otomatik doldurulmuştur."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <form onSubmit={(e) => e.preventDefault()}>
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Doğrulama Kodu <span style={{ color: 'var(--error)' }}>*</span>
            </Text>
            <Input
              size="large"
              placeholder="123456"
              value={code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(value);
                setCodeError('');
              }}
              maxLength={6}
              style={{
                borderColor: codeError ? 'var(--error)' : undefined,
                fontSize: '20px',
                letterSpacing: '8px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}
            />
            {codeError && (
              <div className="auth-alert-error" style={{ marginTop: 10 }} role="alert">
                {codeError}
              </div>
            )}
          </div>

          <Button
            type="primary"
            size="large"
            block
            loading={loading}
            onClick={handleSubmit}
            className="btn-primary"
            style={{ marginBottom: '16px' }}
          >
            Doğrula ve Hesabı Aktifleştir
          </Button>

          <Link to="/login">
            <Button type="link" block style={{ color: 'var(--text-secondary)' }}>
              <ArrowLeftOutlined /> Giriş Sayfasına Dön
            </Button>
          </Link>
        </form>
      </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
